import React, { Suspense } from 'react'
import { Heading, Icon, Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Loading } from '@pandiafi/components'

import { View, RoundLink } from '../../components'
import { useContractPaths } from '../../hooks/useContractPaths/useContractPaths'
import routePath from '../../router/routePath'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { TContact } from '../../../common/validations/contractContacts'
import { ContactCategory } from '../../../common/types'
import { ContactInfoSidebarDataLoader } from './ContactInfoSidebarDataLoader'

import { ContactInfoNoticesList } from 'components/ContactInfoNoticesList'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

export const ContactInfoView = ({ contacts }: { contacts: TContact[] }) => {
  const { t } = useTranslation(['common', 'titles', 'contact'])
  const { frontPagePath } = useContractPaths()
  const navigate = useNavigate()
  const { currentContractId } = useCurrentContract()
  const modules = useModuleConfig()

  const {
    propertyManager,
    propertyMaintenance,
    customerService,
    salesRepresentative,
    residentsCommittee,
  } = resolveContacts(contacts)

  const hasDefectNotice =
    (modules.defectNotice.enabled &&
      !modules.defectNotice.customDefectNoticeLinkUrl) ??
    false
  const hasFeedbackOrQuestion =
    modules.defectNotice.feedback || modules.defectNotice.question

  return (
    <View>
      <View.Header>
        <View.HeaderLink to={frontPagePath}>
          {t('common:views.home')}
        </View.HeaderLink>
        <View.HeaderTitle>{t('contact:contactInformation')}</View.HeaderTitle>
      </View.Header>

      <View.Sidebar>
        {hasDefectNotice && hasFeedbackOrQuestion && (
          <Suspense fallback={<Loading variant="details" />}>
            <ContactInfoSidebarDataLoader>
              {({ feedbackAndQuestionNotices }) => (
                <ContactInfoNoticesList notices={feedbackAndQuestionNotices} />
              )}
            </ContactInfoSidebarDataLoader>
          </Suspense>
        )}
      </View.Sidebar>
      <View.Content>
        <div>
          {propertyMaintenance && (
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-base tablet:text-xl font-medium my-4">
                    {propertyMaintenance.officeName}
                  </p>
                  <p className="my-1">{propertyMaintenance.name}</p>
                  {propertyMaintenance.phoneNumber && (
                    <p className="text-sm lg:text-base my-1">
                      {propertyMaintenance.phoneNumber}
                    </p>
                  )}
                  <p className="text-sm lg:text-base my-1 underline">
                    <a href={`mailto:${propertyMaintenance.email}`}>
                      {propertyMaintenance.email}
                    </a>
                  </p>
                  <p className="text-xs text-common-gray-6 mt-2">
                    {t('contact:propertyMaintenance')}
                  </p>
                </div>
                <div className="flex">
                  {propertyMaintenance.phoneNumber && (
                    <RoundLink
                      className="flex items-center mr-3 min-tablet:hidden"
                      href={`tel:${propertyMaintenance.phoneNumber}`}
                    >
                      <Icon icon="phone" size="lg" className="w-6" />
                    </RoundLink>
                  )}
                  <RoundLink
                    className="flex items-center"
                    href={`mailto:${propertyMaintenance.email}`}
                  >
                    <Icon icon="email" size="lg" className="w-6" />
                  </RoundLink>
                </div>
              </div>
            </div>
          )}

          {propertyManager && (
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-base tablet:text-xl font-medium my-4">
                    {propertyManager.officeName}
                  </p>
                  <p className="my-1">{propertyManager.name}</p>
                  {propertyManager.phoneNumber && (
                    <p className="text-sm lg:text-base my-1">
                      {propertyManager.phoneNumber}
                    </p>
                  )}
                  <p className="text-sm lg:text-base my-1 underline">
                    <a href={`mailto:${propertyManager.email}`}>
                      {propertyManager.email}
                    </a>
                  </p>
                  <p className="text-xs text-common-gray-6 mt-2">
                    {t('contact:propertyManager')}
                  </p>
                </div>
                <div className="flex">
                  {propertyManager.phoneNumber && (
                    <RoundLink
                      className="flex items-center mr-3 min-tablet:hidden"
                      href={`tel:${propertyManager.phoneNumber}`}
                    >
                      <Icon icon="phone" size="lg" className="w-6" />
                    </RoundLink>
                  )}
                  <RoundLink
                    className="flex items-center"
                    href={`mailto:${propertyManager.email}`}
                  >
                    <Icon icon="email" size="lg" className="w-6" />
                  </RoundLink>
                </div>
              </div>
            </div>
          )}

          {customerService && (
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-base tablet:text-xl font-medium my-4">
                    {t('contact:customerService')}
                  </p>
                  {customerService.phoneNumber && (
                    <p className="text-sm lg:text-base my-1">
                      {customerService.phoneNumber}
                    </p>
                  )}
                  <p className="text-sm lg:text-base my-1 underline">
                    <a href={`mailto:${customerService.email}`}>
                      {customerService.email}
                    </a>
                  </p>
                </div>
                <div className="flex">
                  {customerService.phoneNumber && (
                    <RoundLink
                      className="flex items-center mr-3 min-tablet:hidden"
                      href={`tel:${customerService.phoneNumber}`}
                    >
                      <Icon icon="phone" size="lg" className="w-6" />
                    </RoundLink>
                  )}
                  <RoundLink
                    className="flex items-center"
                    href={`mailto:${customerService.email}`}
                  >
                    <Icon icon="email" size="lg" className="w-6" />
                  </RoundLink>
                </div>
              </div>
            </div>
          )}

          {hasDefectNotice && hasFeedbackOrQuestion && (
            <div className="mb-8 flex flex-col sm:flex-row gap-4">
              {modules.defectNotice.feedback && (
                <Button
                  onClick={() =>
                    navigate(
                      routePath.feedback.replace(
                        ':contractId',
                        currentContractId
                      ),
                      { state: { from: 'contact' } }
                    )
                  }
                  variant="secondary"
                  className="w-full"
                >
                  {t('contact:feedback.button')}
                </Button>
              )}
              {modules.defectNotice.question && (
                <Button
                  onClick={() =>
                    navigate(
                      routePath.question.replace(
                        ':contractId',
                        currentContractId
                      ),
                      { state: { from: 'contact' } }
                    )
                  }
                  variant="secondary"
                  className="w-full"
                >
                  {t('contact:question.button')}
                </Button>
              )}
            </div>
          )}

          {salesRepresentative && (
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-base tablet:text-xl font-medium my-4">
                    {t('common:buildingInfo.sales')}
                  </p>
                  <p className="my-1">{salesRepresentative.name}</p>
                  {salesRepresentative.phoneNumber && (
                    <p className="text-sm lg:text-base my-1">
                      {salesRepresentative.phoneNumber}
                    </p>
                  )}
                  <p className="text-sm lg:text-base my-1 underline">
                    <a href={`mailto:${salesRepresentative.email}`}>
                      {salesRepresentative.email}
                    </a>
                  </p>
                  <p className="text-xs text-common-gray-6 mt-2">
                    {t('common:buildingInfo.sales')}
                  </p>
                </div>
                <div className="flex">
                  {salesRepresentative.phoneNumber && (
                    <RoundLink
                      className="flex items-center mr-3 min-tablet:hidden"
                      href={`tel:${salesRepresentative.phoneNumber}`}
                    >
                      <Icon icon="phone" size="lg" className="w-6" />
                    </RoundLink>
                  )}
                  <RoundLink
                    className="flex items-center"
                    href={`mailto:${salesRepresentative.email}`}
                  >
                    <Icon icon="email" size="lg" className="w-6" />
                  </RoundLink>
                </div>
              </div>
            </div>
          )}

          {residentsCommittee && residentsCommittee.length > 0 && (
            <div className="mb-8">
              <Heading element="h2" size="l" className="my-4">
                {t('contact:residentsCommittee')}
              </Heading>
              {residentsCommittee.map((member) => (
                <div key={member.id} className="mb-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-common-gray-6">
                        {member.serviceRole}
                      </p>
                    </div>
                    <div className="flex">
                      {member.phoneNumber && (
                        <RoundLink
                          className="flex items-center mr-3 min-tablet:hidden"
                          href={`tel:${member.phoneNumber}`}
                        >
                          <Icon icon="phone" size="lg" className="w-6" />
                        </RoundLink>
                      )}
                      {member.email && (
                        <RoundLink
                          className="flex items-center"
                          href={`mailto:${member.email}`}
                        >
                          <Icon icon="email" size="lg" className="w-6" />
                        </RoundLink>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(!contacts || contacts.length === 0) && (
            <div className="text-center py-8 text-common-gray-6">
              {t('common:buildingInfo.noContactInformation')}
            </div>
          )}
        </div>
      </View.Content>
    </View>
  )
}

const resolveContacts = (contacts?: TContact[]) => {
  const propertyManager = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MANAGEMENT
  )
  const propertyMaintenance = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MAINTENANCE
  )
  const customerService = contacts?.find(
    (c) => c.contactCategory === ContactCategory.CUSTOMER_SERVICE
  )
  const salesRepresentative = contacts?.find(
    (c) => c.contactCategory === ContactCategory.SALES_REPRESENTATIVE
  )

  // Filter out members of the residents' committee
  const residentsCommittee = contacts?.filter(
    (c) => c.contactCategory === ContactCategory.RESIDENT_COMMITTEE
  )

  return {
    propertyManager,
    propertyMaintenance,
    customerService,
    salesRepresentative,
    residentsCommittee,
  }
}
